"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

type Step = "mobile" | "otp" | "email" | "emailOtp" | "digilocker" | "success";
export type LegalDoc = "terms" | "privacy";

/* Dark, unified with the Dost site. One mobile field routes new vs returning. */
const LABEL = "text-[13px] font-medium text-white/45";
const INPUT =
  "mt-2.5 w-full rounded-2xl border border-white/[0.1] bg-[#1a1a1a] px-4 py-4 text-[16px] text-white outline-none transition placeholder:text-white/25 focus:border-white/30";
const PRIMARY =
  "mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-4 text-[16px] font-semibold text-[#0B0C0E] transition hover:bg-[#E9E9E6] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-55";
const GHOST = "text-[13.5px] text-white/45 transition hover:text-white/80";

async function post<T = Record<string, unknown>>(url: string, body: unknown): Promise<{ ok: boolean; data: T }> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as T & { error?: string };
    return { ok: res.ok && !(data as { error?: string }).error, data };
  } catch {
    return { ok: false, data: { error: "Network error. Could not reach Dost." } as T };
  }
}

export function LoginForm({ onOpenLegal }: { onOpenLegal?: (doc: LegalDoc) => void } = {}) {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") || "/console";
  const { refresh } = useAuth();

  const [step, setStep] = useState<Step>("mobile");
  const [mode, setMode] = useState<"signin" | "onboard">("signin");
  const [mobile, setMobile] = useState("");
  const [appRefId, setAppRefId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dlUrl, setDlUrl] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const pollRef = useRef<number | null>(null);

  const startCooldown = useCallback(() => setResendIn(30), []);
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);
  useEffect(() => {
    if (step === "otp" || step === "emailOtp") startCooldown();
  }, [step, startCooldown]);

  const finishSignedIn = useCallback(async () => {
    setStep("success");
    await refresh();
    window.setTimeout(() => router.replace(next), 1200);
  }, [refresh, router, next]);

  // ── step 1: mobile ────────────────────────────────────────────────
  async function submitMobile(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[6-9]\d{9}$/.test(mobile)) return setError("Enter a valid 10-digit mobile number.");
    setLoading(true);
    const { ok, data } = await post<{ error?: string; mode?: string; appRefId?: string }>("/api/auth/start", {
      mobileNumber: mobile,
    });
    setLoading(false);
    if (!ok) return setError(data.error ?? "Could not send the code.");
    setMode(data.mode === "onboard" ? "onboard" : "signin");
    setAppRefId(data.appRefId ?? null);
    setOtp("");
    setStep("otp");
  }

  // ── step 2: mobile OTP → sign in (returning) or continue onboarding (new) ──
  async function submitOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const code = otp.replace(/\D/g, "");
    if (code.length < 4) return setError("Enter the code we sent you.");
    setLoading(true);

    if (mode === "signin") {
      const { ok, data } = await post<{ error?: string }>("/api/auth/signin", {
        mobileNumber: mobile,
        otp: code,
      });
      setLoading(false);
      if (!ok) return setError(data.error ?? "Could not sign in.");
      await finishSignedIn();
      return;
    }

    const { ok, data } = await post<{ error?: string; nextStep?: string }>("/api/auth/verify-mobile", {
      appRefId,
      otp: code,
    });
    setLoading(false);
    if (!ok) return setError(data.error ?? "Invalid or expired code.");
    setOtp("");
    if (data.nextStep === "DIGILOCKER") beginDigilocker();
    else setStep("email");
  }

  // ── step 3: email ─────────────────────────────────────────────────
  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Enter a valid email address.");
    setLoading(true);
    const { ok, data } = await post<{ error?: string }>("/api/auth/email", { appRefId, emailAddress: email });
    setLoading(false);
    if (!ok) return setError(data.error ?? "Could not send the code.");
    setOtp("");
    setStep("emailOtp");
  }

  async function submitEmailOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const code = otp.replace(/\D/g, "");
    if (code.length < 4) return setError("Enter the code we emailed you.");
    setLoading(true);
    const { ok, data } = await post<{ error?: string }>("/api/auth/verify-email", { appRefId, otp: code });
    setLoading(false);
    if (!ok) return setError(data.error ?? "Invalid or expired code.");
    beginDigilocker();
  }

  // ── step 5: DigiLocker (Aadhaar) ──────────────────────────────────
  async function beginDigilocker() {
    setStep("digilocker");
    setError(null);
    const redirectUrl = `${window.location.origin}/login`;
    const { ok, data } = await post<{ error?: string; url?: string }>("/api/auth/digilocker/init", {
      appRefId,
      redirectUrl,
    });
    if (!ok || !data.url) {
      setError(data.error ?? "Could not start DigiLocker.");
      return;
    }
    setDlUrl(data.url);
    window.open(data.url, "_blank", "noopener,noreferrer");
    startPolling();
  }

  const startPolling = useCallback(() => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = window.setInterval(async () => {
      tries += 1;
      const { data } = await post<{ status?: string; error?: string }>("/api/auth/digilocker/status", { appRefId });
      if (data.status === "success") {
        if (pollRef.current) window.clearInterval(pollRef.current);
        await finishSignedIn();
      } else if (data.error) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setError(data.error);
      } else if (tries > 60) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setError("Verification timed out. Reopen DigiLocker to try again.");
      }
    }, 3000);
  }, [appRefId, finishSignedIn]);

  useEffect(() => () => void (pollRef.current && window.clearInterval(pollRef.current)), []);

  function reset() {
    if (pollRef.current) window.clearInterval(pollRef.current);
    setStep("mobile");
    setError(null);
    setOtp("");
    setDlUrl(null);
  }

  // Resend re-calls the same send endpoint; the backend keeps the appRefId.
  async function resendMobile() {
    if (resendIn > 0) return;
    setError(null);
    startCooldown();
    const { data } = await post<{ appRefId?: string }>("/api/auth/start", { mobileNumber: mobile });
    if (data.appRefId) setAppRefId(data.appRefId);
  }
  async function resendEmail() {
    if (resendIn > 0) return;
    setError(null);
    startCooldown();
    await post("/api/auth/email", { appRefId, emailAddress: email });
  }

  const otpField = (
    onSubmit: (e: React.FormEvent) => void,
    sentTo: string,
    submitLabel: string,
    onResend: () => void,
  ) => (
    <form onSubmit={onSubmit}>
      <div className="text-[27px] font-semibold tracking-tight text-white">Enter the code</div>
      <p className="mt-2 text-[15px] leading-relaxed text-white/45">
        Sent to {sentTo} · <button type="button" onClick={reset} className="text-white/70 hover:text-white">edit</button>
      </p>
      <input
        autoFocus
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        placeholder="• • • • • •"
        className={`${INPUT} mt-6 text-center text-[22px] tracking-[0.5em]`}
      />
      {error && <p className="mt-3 text-[13px] text-[#f0999b]">{error}</p>}
      <button type="submit" disabled={loading} className={PRIMARY}>
        {loading ? "Verifying…" : submitLabel}
      </button>
      <p className="mt-4 text-center text-[13px] text-white/40">
        Didn&rsquo;t get it?{" "}
        {resendIn > 0 ? (
          <span className="text-white/55">Resend in {resendIn}s</span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            className="font-medium text-white/70 underline-offset-2 transition hover:text-white hover:underline"
          >
            Resend code
          </button>
        )}
      </p>
    </form>
  );

  return (
    <div className="w-full max-w-[400px]">
        {step === "mobile" && (
          <form onSubmit={submitMobile}>
            <div className="text-[27px] font-semibold tracking-tight text-white">Sign in to Dost</div>
            <p className="mt-2 text-[15px] leading-relaxed text-white/45">Enter your mobile to continue.</p>
            <label className={`mt-7 block ${LABEL}`}>Mobile number</label>
            <div className="mt-2.5 flex items-center gap-3 rounded-2xl border border-white/[0.1] bg-[#1a1a1a] px-4 focus-within:border-white/30">
              <span className="text-[16px] font-medium text-white/55">+91</span>
              <span className="h-6 w-px bg-white/10" />
              <input
                autoFocus
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                placeholder="98765 43210"
                className="w-full bg-transparent py-4 text-[16px] tracking-wide text-white outline-none placeholder:text-white/25"
              />
            </div>
            {error && <p className="mt-3 text-[13px] text-[#f0999b]">{error}</p>}
            <button type="submit" disabled={loading} className={PRIMARY}>
              {loading ? "Sending…" : "Continue"}
            </button>
            <p className="mt-4 text-center text-[13.5px] leading-relaxed text-white/35">
              New here? No forms to fill. Just enter your number and we&rsquo;ll take care of the rest.
            </p>
          </form>
        )}

        {step === "otp" && otpField(submitOtp, `+91 ${mobile}`, mode === "signin" ? "Sign in" : "Continue", resendMobile)}

        {step === "email" && (
          <form onSubmit={submitEmail}>
            <div className="text-[27px] font-semibold tracking-tight text-white">Add your email</div>
            <p className="mt-2 text-[15px] leading-relaxed text-white/45">We&rsquo;ll send a quick verification code.</p>
            <label className={`mt-5 block ${LABEL}`}>Email address</label>
            <input
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="you@company.com"
              className={INPUT}
            />
            {error && <p className="mt-3 text-[13px] text-[#f0999b]">{error}</p>}
            <button type="submit" disabled={loading} className={PRIMARY}>
              {loading ? "Sending…" : "Send code"}
            </button>
          </form>
        )}

        {step === "emailOtp" && otpField(submitEmailOtp, email, "Verify email", resendEmail)}

        {step === "digilocker" && (
          <div>
            <div className="text-[27px] font-semibold tracking-tight text-white">Verify your identity</div>
            <p className="mt-2 text-[15px] leading-relaxed text-white/45">
              Aadhaar via DigiLocker, secure and one time.
            </p>
            <div className="mt-6 space-y-3">
              <Row done label="Mobile verified" />
              <Row done label="Email verified" />
              <Row label="DigiLocker pending" />
            </div>
            {error ? (
              <p className="mt-4 text-[13px] text-[#f0999b]">{error}</p>
            ) : (
              <p className="mt-4 text-[12px] text-white/35">
                {dlUrl ? "Complete DigiLocker in the opened tab. Waiting for verification…" : "Starting DigiLocker…"}
              </p>
            )}
            {dlUrl && (
              <a href={dlUrl} target="_blank" rel="noopener noreferrer" className={PRIMARY}>
                Verify with DigiLocker
              </a>
            )}
            <button type="button" onClick={reset} className={`mt-4 block w-full text-center ${GHOST}`}>
              Start over
            </button>
          </div>
        )}

        {step === "success" && (
          <div className="py-6 text-center">
            <div className="success-pop mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#4ade80]/15">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <div className="mt-6 text-[26px] font-semibold tracking-tight text-white">You&rsquo;re verified</div>
            <div className="mt-3 flex items-center justify-center gap-2.5 text-[14.5px] text-white/50">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/15 border-t-white/70" />
              Taking you to your console…
            </div>
          </div>
        )}

        {step !== "success" && (
        <p className="mt-5 text-center text-[12.5px] leading-relaxed text-white/30">
          By continuing you agree to{" "}
          <button
            type="button"
            onClick={() => onOpenLegal?.("terms")}
            className="text-white/50 underline-offset-2 transition-colors hover:text-white/85 hover:underline"
          >
            Terms
          </button>{" "}
          &amp;{" "}
          <button
            type="button"
            onClick={() => onOpenLegal?.("privacy")}
            className="text-white/50 underline-offset-2 transition-colors hover:text-white/85 hover:underline"
          >
            Privacy
          </button>
          .
        </p>
        )}
    </div>
  );
}

function Row({ label, done }: { label: string; done?: boolean }) {
  return (
    <div className="flex items-center gap-2.5 text-[12.5px]">
      <span
        className={`flex h-[18px] w-[18px] items-center justify-center rounded-full text-[11px] ${
          done ? "bg-[#6ee7a0]/15 text-[#6ee7a0]" : "border border-white/15 text-white/30"
        }`}
      >
        {done ? "✓" : "•"}
      </span>
      <span className={done ? "text-white/70" : "text-white/40"}>{label}</span>
    </div>
  );
}
